// src/utils/threeUtils.ts
import * as THREE from 'three';
// import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js'; // Not used here
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// WGS84 ellipsoid parameters
const WGS84_A = 6378137.0; // Semi-major axis (equatorial radius) in meters
const WGS84_F = 1 / 298.257223563; // Flattening
const WGS84_B = WGS84_A * (1 - WGS84_F); // Semi-minor axis (polar radius)
const WGS84_E2 = 2 * WGS84_F - WGS84_F * WGS84_F; // Square of first eccentricity

/**
 * Converts Geodetic coordinates (latitude, longitude, altitude) to ECEF (Earth-Centered, Earth-Fixed) coordinates.
 * Input: [latitude (degrees), longitude (degrees), altitude (meters)]
 * Output: THREE.Vector3 { x, y, z } in ECEF meters
 */
export function convertFromExternalCoords(coords: [number, number] | [number, number, number]): THREE.Vector3 {
    const lat = coords[0];
    const lon = coords[1];
    const alt = coords.length > 2 ? coords[2]! : 0; // Altitude above the WGS84 ellipsoid

    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinLon = Math.sin(lonRad);
    const cosLon = Math.cos(lonRad);

    // Radius of curvature in the prime vertical
    const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);

    // Calculate ECEF coordinates
    const x = (N + alt) * cosLat * cosLon;
    const y = (N + alt) * cosLat * sinLon;
    const z = ((WGS84_B * WGS84_B) / (WGS84_A * WGS84_A) * N + alt) * sinLat;

    return new THREE.Vector3(x, y, z);
}

/**
 * Convenience wrapper for convertFromExternalCoords that returns a raw array.
 * Converts Geodetic coordinates (latitude, longitude, altitude) to an ECEF coordinate array.
 * @param lat Latitude in degrees.
 * @param lon Longitude in degrees.
 * @param elevation Altitude in meters.
 * @returns [x, y, z] ECEF coordinate array in meters.
 */
export function latLonElevToECEF(lat: number, lon: number, elevation: number): [number, number, number] {
    return convertFromExternalCoords([lat, lon, elevation]).toArray() as [number, number, number];
}


/**
 * Converts ECEF (Earth-Centered, Earth-Fixed) coordinates to Geodetic coordinates (latitude, longitude, altitude).
 * This is a direct implementation based on the Heiskanen and Moritz formula.
 * Input: [x, y, z] ECEF meters
 * Output: [latitude (degrees), longitude (degrees), altitude (meters)]
 */
export function ecefToGeodetic(ecef: [number, number, number]): [number, number, number] {
    const [x, y, z] = ecef;
    const a = WGS84_A;
    const b = WGS84_B;
    const e2 = WGS84_E2;
    const ep2 = (a * a - b * b) / (b * b); // second eccentricity squared

    const p = Math.sqrt(x * x + y * y);
    const theta = Math.atan2(z * a, p * b);

    const sinTheta = Math.sin(theta);
    const cosTheta = Math.cos(theta);

    const lon = Math.atan2(y, x);
    const lat = Math.atan2(
        (z + ep2 * b * sinTheta * sinTheta * sinTheta),
        (p - e2 * a * cosTheta * cosTheta * cosTheta)
    );

    const sinLat = Math.sin(lat);
    const cosLat = Math.cos(lat);
    const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
    const alt = p / cosLat - N;

    // Convert radians to degrees for return
    const latDeg = (lat * 180) / Math.PI;
    const lonDeg = (lon * 180) / Math.PI;

    return [latDeg, lonDeg, alt];
}


/**
 * Converts ECEF coordinates [x, y, z] used by the API/backend
 * to the internal Three.js world coordinate system.
 * This mapping depends on how the 3D scene is oriented relative to ECEF.
 * Common convention: ECEF X -> Three +X, ECEF Y -> Three -Z, ECEF Z -> Three +Y (Y-up)
 * Input: [x_ecef, y_ecef, z_ecef]
 * Output: Three.js Vector3(x_three, y_three, z_three)
 */
export const convertToInternalCoords = (ecefCoords: [number, number, number]): THREE.Vector3 => {
  // Assuming Y-up convention for Three.js scene relative to ECEF:
  return new THREE.Vector3(
    ecefCoords[0], // ECEF X maps to Three X
    ecefCoords[2], // ECEF Z maps to Three Y (Up)
   -ecefCoords[1]  // ECEF Y maps to Three -Z (Depth/Forward)
  );
};

/**
 * Converts internal Three.js world coordinates Vector3(x, y, z)
 * back to ECEF coordinates [x, y, z] for the API/backend.
 * This is the inverse of convertToInternalCoords.
 * Input: Three.js Vector3(x_three, y_three, z_three)
 * Output: ECEF [x_ecef, y_ecef, z_ecef]
 */
export const convertToExternalCoords = (worldVector: THREE.Vector3): [number, number, number] => {
  // Assuming Y-up convention:
  return [
    worldVector.x,    // Three X maps to ECEF X
   -worldVector.z,   // Three -Z maps to ECEF Y
    worldVector.y     // Three Y maps to ECEF Z
  ];
};

/**
 * Converts world coordinates to local coordinates relative to a specified world origin.
 * Helps avoid floating point precision issues when dealing with large world coordinates (like ECEF).
 * The local origin MUST be in world coordinates.
 */
export const worldToLocal = (worldPoint: THREE.Vector3, localOriginWorld: THREE.Vector3 | null): THREE.Vector3 => {
  if (!localOriginWorld) {
      console.warn("worldToLocal called without localOriginWorld, returning original worldPoint.");
      return worldPoint.clone();
  }
  return new THREE.Vector3().subVectors(worldPoint, localOriginWorld);
};

/**
 * Converts local coordinates (relative to a world origin) back to world coordinates.
 * Inverse of worldToLocal function.
 * The local origin MUST be in world coordinates.
 */
export const localToWorld = (localPoint: THREE.Vector3, localOriginWorld: THREE.Vector3 | null): THREE.Vector3 => {
  if (!localOriginWorld) {
       console.warn("localToWorld called without localOriginWorld, returning original localPoint.");
      return localPoint.clone();
    }
  return new THREE.Vector3().addVectors(localPoint, localOriginWorld);
};

/**
 * Creates a Material UI-style text sprite label.
 */
export const createTextSprite = (text: string): THREE.Sprite => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get canvas context');
    
    // Styling constants
    const backgroundColor = '#F6B12D';
    const textColor = '#000000';
    const outlineColor = '#000000';
    const outlineWidth = 2;
    const fontSize = 36;
    const fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const paddingX = 40;
    const paddingY = 20;
    
    // Set up font first to measure text
    context.font = `bold ${fontSize}px ${fontFamily}`;
    
    // Measure text to determine canvas size
    const textMetrics = context.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = fontSize;
    
    // Calculate canvas dimensions based on text size
    canvas.width = textWidth + paddingX * 2;
    canvas.height = textHeight + paddingY * 2;
    
    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Re-set font after canvas resize
    context.font = `bold ${fontSize}px ${fontFamily}`;
    
    // Calculate pill radius (half the height)
    const pillRadius = canvas.height / 2;
    
    // Draw pill shape with outline
    context.fillStyle = backgroundColor;
    context.strokeStyle = outlineColor;
    context.lineWidth = outlineWidth;
    
    // Create pill shape path
    context.beginPath();
    context.arc(pillRadius, pillRadius, pillRadius - outlineWidth/2, Math.PI/2, 3*Math.PI/2);
    context.lineTo(canvas.width - pillRadius, outlineWidth/2);
    context.arc(canvas.width - pillRadius, pillRadius, pillRadius - outlineWidth/2, 3*Math.PI/2, Math.PI/2);
    context.lineTo(pillRadius, canvas.height - outlineWidth/2);
    context.closePath();
    
    // Fill and stroke the pill
    context.fill();
    context.stroke();
    
    // Draw text (centered)
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = textColor;
    context.fillText(text, canvas.width / 2, canvas.height / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(spriteMaterial);
    
    // Scale sprite proportionally to canvas size
    const scale = 0.02; // Adjust this to control overall size
    sprite.scale.set(canvas.width * scale, canvas.height * scale, 1);

    sprite.renderOrder = 3000; 

    return sprite;
};

/**
 * Creates a question mark sprite for uncertain gardens.
 */
export const createQuestionMarkSprite = (): THREE.Sprite => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get canvas context');
    
    const size = 128; // Canvas texture size
    canvas.width = size;
    canvas.height = size;
    
    // Draw circle background
    const backgroundColor = '#FFFFFF';
    const outlineColor = '#000000';
    const outlineWidth = 8;
    context.fillStyle = backgroundColor;
    context.strokeStyle = outlineColor;
    context.lineWidth = outlineWidth;
    
    context.beginPath();
    context.arc(size / 2, size / 2, size / 2 - outlineWidth, 0, Math.PI * 2);
    context.fill();
    context.stroke();

    // Draw question mark
    const fontSize = 80;
    const fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    context.font = `bold ${fontSize}px ${fontFamily}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = '#000000';
    context.fillText('?', size / 2, size / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(spriteMaterial);
    
    // Scale sprite proportionally
    const scale = 0.01; // Adjust this to control overall size
    sprite.scale.set(size * scale, size * scale, 1);

    sprite.renderOrder = 3001; // Render on top of other sprites

    return sprite;
};

/**
 * Cleans up Three.js resources associated with an object and removes it from parent.
 * Safe disposal of geometries, materials, and textures.
 */
export const disposeObject3D = (object: THREE.Object3D) => {
  if (!object) return;

  if (object.parent) {
    object.parent.remove(object);
  }

  if ((object as any).isGroup || object instanceof THREE.Scene) {
      const children = [...object.children];
      children.forEach(disposeObject3D);
  }

  if ((object as any).geometry && typeof (object as any).geometry.dispose === 'function') {
      try {
        (object as any).geometry.dispose();
      } catch (err) {
           console.warn("Error disposing geometry:", err, object);
      }
  }

  if ((object as any).material) {
    const materials = Array.isArray((object as any).material) ? (object as any).material : [(object as any).material];
    materials.forEach((material: any) => {
      if (!material) return;
      try {
          Object.values(material).forEach((value: any) => {
            if (value instanceof THREE.Texture && typeof value.dispose === 'function') {
              value.dispose();
            }
          });
          if (typeof material.dispose === 'function') {
             material.dispose();
          }
      } catch (err) {
           console.warn("Error disposing material or its textures:", err, material);
      }
    });
  }

  if (typeof (object as any).clear === 'function') {
     (object as any).clear();
  }
};

/**
 * Updates the resolution of LineMaterial instances in a scene.
 * Necessary when the canvas resizes.
 */
export const updateLineMaterialsResolution = (scene: THREE.Scene, width: number, height: number) => {
    scene.traverse((object) => {
        if (object instanceof Line2 && object.material instanceof LineMaterial) {
             object.material.resolution.set(width, height);
        }
    });
};

/**
 * An interface representing the minimal data needed to find the elevation for a point.
 */
export interface GardenPointData {
  gardenUid: string;
  ecefPosition: [number, number, number];
}

/**
 * Calculates the most likely ground/surface elevation for one or more gardens by raycasting a set of points
 * (ideally sun markers) against a 3D object. It uses clustering to robustly handle outliers, such as
 * stray points on the ground when most points are on a roof terrace.
 *
 * @param targetObject The THREE.Object3D (e.g., a Group containing 3D tiles) to raycast against.
 * @param localOrigin The current world-coordinate origin of the scene, used for local coordinate conversion.
 * @param pointsToTest An array of GardenPointData objects, typically derived from sun markers.
 * @returns A Map where keys are garden UIDs and values are the calculated median elevation in meters.
 */
export const calculateGroundElevations = (
  targetObject: THREE.Object3D,
  localOrigin: THREE.Vector3,
  pointsToTest: GardenPointData[]
): Map<string, number> => {
  if (!targetObject || pointsToTest.length === 0) {
    return new Map();
  }

  targetObject.updateMatrixWorld(true);
  const raycaster = new THREE.Raycaster();
  const allIntersectPointsByGarden = new Map<string, THREE.Vector3[]>();

  // 1. Raycast for every single point and collect all valid intersection points, grouped by garden.
  pointsToTest.forEach(pointData => {
    const worldPos = convertToInternalCoords(pointData.ecefPosition);
    const localPos = worldPos.clone().sub(localOrigin);
    const upVector = worldPos.clone().normalize();
    const downVector = upVector.clone().negate();

    const startPoint = localPos.clone().add(upVector.clone().multiplyScalar(500));
    raycaster.set(startPoint, downVector);

    const intersects = raycaster.intersectObject(targetObject, true);

    if (intersects.length > 0) {
      let lowestIntersect = intersects[0];
      for (let i = 1; i < intersects.length; i++) {
        if (intersects[i].point.y < lowestIntersect.point.y) {
          lowestIntersect = intersects[i];
        }
      }

      if (!allIntersectPointsByGarden.has(pointData.gardenUid)) {
        allIntersectPointsByGarden.set(pointData.gardenUid, []);
      }
      allIntersectPointsByGarden.get(pointData.gardenUid)!.push(lowestIntersect.point);
    }
  });

  const finalElevations = new Map<string, number>();

  // 2. For each garden, analyze its collected points to find the correct elevation.
  allIntersectPointsByGarden.forEach((points, gardenId) => {
    if (points.length === 0) return;

    points.sort((a, b) => a.y - b.y);

    const CLUSTER_THRESHOLD = 3.0; 

    const elevationClusters: THREE.Vector3[][] = [];
    if (points.length > 0) {
      let currentCluster = [points[0]];
      for (let i = 1; i < points.length; i++) {
        if (points[i].y - currentCluster[0].y < CLUSTER_THRESHOLD) {
          currentCluster.push(points[i]);
        } else {
          elevationClusters.push(currentCluster);
          currentCluster = [points[i]];
        }
      }
      elevationClusters.push(currentCluster);
    }

    if(elevationClusters.length === 0) return;

    let largestCluster = elevationClusters[0] || [];
    for (const cluster of elevationClusters) {
      if (cluster.length > largestCluster.length) {
        largestCluster = cluster;
      }
    }

    if(largestCluster.length > 0) {
        const medianPoint = largestCluster[Math.floor(largestCluster.length / 2)];

        const finalWorldPoint = medianPoint.clone().add(localOrigin);
        const finalEcef = convertToExternalCoords(finalWorldPoint);
        const [, , finalElevation] = ecefToGeodetic(finalEcef);
        finalElevations.set(gardenId, finalElevation);
    }
  });

  return finalElevations;
};